/**
 * The show-once dialog for a webhook source's signing secret (decision 62).
 *
 * ONE component for both screens that can produce a secret: the Connect
 * Source wizard after a webhook create (context 'created') and the source's
 * Settings card after a confirmed Rotate (context 'rotated'). The secret is
 * passed in from the parent's component state and nowhere else — never the
 * query cache, storage, the URL, console or a toast (the copy toast carries
 * words only). Closing the dialog, any way, calls `onOpenChange(false)`; the
 * parent drops the secret from its state there, so it cannot be shown again.
 *
 * Not the secret dialog of the Admin › Settings › API webhooks screen: that is
 * the tenant's outbound webhooks noun with its own grace model; this secret
 * has no grace — the previous one stops working at once.
 */
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
// The one clipboard path that reports whether the write happened (#674).
import { copyTextToClipboard } from '@/components/chat/-table-copy-helpers';

/**
 * The dialog's `onOpenChange` for a parent that holds the secret: closing it,
 * any way (the button, Escape, the overlay), drops the secret from the
 * parent's state and resets the mutation that returned it — so neither the
 * screen nor the TanStack mutation cache keeps a copy. Opening changes nothing.
 */
export function dropSecretOnClose(clearSecret: () => void, resetMutation: () => void): (open: boolean) => void {
  return (open) => {
    if (open) return;
    clearSecret();
    resetMutation();
  };
}

/**
 * Copy, and say what actually happened (LB6b-T6-R2). A secret shown once must
 * never be reported copied when the browser refused the write — or, on a
 * plain-HTTP origin, has no clipboard at all — because the next thing the
 * person does is close the dialog, and the secret is gone. The toasts carry
 * words only, never the value.
 */
export async function copyWithReport(text: string, what: 'secret' | 'url'): Promise<void> {
  const result = await copyTextToClipboard(text);
  if (result.kind === 'ok') {
    toast.success(what === 'secret' ? 'Secret copied' : 'URL copied');
    return;
  }
  toast.error(
    what === 'secret'
      ? 'The secret was not copied. Select it and copy it by hand before you close this dialog.'
      : 'The URL was not copied. Select it and copy it by hand.',
  );
}

export interface SigningSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secret: string;
  webhookUrl: string;
  context: 'created' | 'rotated';
}

export function SigningSecretDialog({ open, onOpenChange, secret, webhookUrl, context }: SigningSecretDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{context === 'rotated' ? 'New signing secret' : 'Signing secret'}</DialogTitle>
          <DialogDescription>
            Sign each delivery with HMAC-SHA256 over the raw body and send it as X-Argus-Signature: sha256=&lt;hex&gt;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Webhook URL</Label>
            <div className="flex gap-2">
              <Input value={webhookUrl} readOnly className="h-8 text-xs font-mono" aria-label="Webhook URL" />
              <Button
                variant="ghost"
                size="sm"
                title="Copy the webhook URL"
                onClick={() => void copyWithReport(webhookUrl, 'url')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Signing secret</Label>
            <div
              className="rounded-md border bg-muted/30 p-3 break-all font-mono text-[11px]"
              data-testid="signing-secret-value"
            >
              {secret}
            </div>
          </div>
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
            You will not see this secret again. If you lose it, rotate it from this source&apos;s Settings.
          </p>
          {context === 'rotated' && (
            <p className="text-xs text-muted-foreground">The previous secret has stopped working.</p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            title="Copy the signing secret to the clipboard"
            onClick={() => void copyWithReport(secret, 'secret')}
          >
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy secret
          </Button>
          <Button title="Close — this secret will not be shown again" onClick={() => onOpenChange(false)}>
            I have saved it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
